/**
=========================================================
* Vue Argon Dashboard 2 PRO - v4.0.0
=========================================================

* Product Page: https://creative-tim.com/product/vue-argon-dashboard-pro
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import store from './store'
import router from './router'
import './assets/css/nucleo-icons.css'
import './assets/css/nucleo-svg.css'
import './assets/css/form-components.css'
import '@/assets/fontawesome-pro-6.6.0/css/all.css'
import VueTilt from 'vue-tilt.js'
import VueSweetalert2 from 'vue-sweetalert2'
import ArgonDashboard from './argon-dashboard'
import { notify, getGenderName } from './utils/globalFunctions'
import { editorOptions } from './utils/editorOptions'

import { quillEditor } from 'vue3-quill'
import 'quill/dist/quill.snow.css'
import EventBus from '@/EventBus'

const appInstance = createApp(App)
const pinia = createPinia(App)

// Global error handler para evitar crashes de DOM
appInstance.config.errorHandler = (err, instance, info) => {
    const errorMessage = err?.message || 'Unknown error';
    
    // Filtrar errores conocidos de manipulación DOM que no son críticos
    const nonCriticalErrors = [
        'null is not an object (evaluating \'editor.value.previousSibling\')',
        'null is not an object (evaluating \'node.parentNode\')',
        'Cannot read properties of null',
        'Cannot read properties of undefined',
        'previousSibling',
        'parentNode'
    ];
    
    const isNonCritical = nonCriticalErrors.some(pattern => errorMessage.includes(pattern));
    
    if (isNonCritical) {
        console.warn('Non-critical DOM error handled:', errorMessage);
        return; // No mostrar error crítico al usuario
    }
    
    // Para otros errores, log pero no crashear
    console.error('Vue Error:', err, info);
};

// Registrar funciones globales
appInstance.config.globalProperties.$notify = notify
appInstance.config.globalProperties.$getGenderName = getGenderName
appInstance.config.globalProperties.$editorOptions = editorOptions
appInstance.config.globalProperties.$eventBus = EventBus

appInstance.use(store)
appInstance.use(router)
appInstance.use(VueTilt)
appInstance.use(VueSweetalert2)
appInstance.use(ArgonDashboard)
appInstance.use(quillEditor)
appInstance.use(pinia)

// Handler global para errores de JavaScript no capturados
window.addEventListener('error', (event) => {
    const errorMessage = event.error?.message || event.message || 'Unknown error';
    
    // Filtrar errores conocidos de DOM
    const nonCriticalErrors = [
        'null is not an object (evaluating \'editor.value.previousSibling\')',
        'null is not an object (evaluating \'node.parentNode\')',
        'Cannot read properties of null',
        'Cannot read properties of undefined',
        'previousSibling',
        'parentNode'
    ];
    
    const isNonCritical = nonCriticalErrors.some(pattern => errorMessage.includes(pattern));
    
    if (isNonCritical) {
        console.warn('Non-critical DOM error handled:', errorMessage);
        event.preventDefault(); // Prevenir que se muestre en consola como error
        return;
    }
    
    console.error('Unhandled JavaScript error:', event.error || event.message);
});

appInstance.mount('#app')
